import {
  UserPlus,
  Users,
  Plug,
  Play,
  Github,
  Slack,
  Mail,
  Calendar,
  Phone,
} from "lucide-react";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { CtaBand } from "@/components/marketing/CtaBand";
import { usePageMeta } from "./usePageMeta";

export function HowItWorks() {
  usePageMeta({
    title: "How it works",
    description:
      "Sign up, pick a subdomain, hire agents from the catalog, connect your tools — and they start working. Four steps, audit-logged end to end.",
  });
  return (
    <>
      <SectionContainer>
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            How it works
          </p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-brand-ink">
            From signup to shipping, in four steps.
          </h1>
          <p className="mt-6 text-lg text-brand-ink/70 leading-relaxed">
            No sales call required for Starter or Growth. Pick a subdomain, hire a role, connect
            your tools, start working.
          </p>
        </div>

        <div className="mt-16 space-y-10 md:space-y-16">
          <Step
            num="01"
            duration="About 30 seconds"
            icon={UserPlus}
            title="Create your workspace"
            body="Sign up with your work email. Pick a subdomain — that's your team URL, your ticket prefix, and your tenant scoping key. Subdomain validation is live as you type."
            Illustration={SubdomainIllustration}
          />
          <Step
            reversed
            num="02"
            duration="2 to 5 minutes per agent"
            icon={Users}
            title="Hire agents from the catalog"
            body="Browse roles by category. Each agent has sensible defaults — pick your guardrails, name your teammate, and they're ready. Add more agents later from inside your workspace."
            Illustration={CatalogIllustration}
          />
          <Step
            num="03"
            duration="One OAuth click per tool"
            icon={Plug}
            title="Connect your tools"
            body="GitHub, Slack, Gmail, Calendar, Twilio, and more. Every connection is scoped — an engineering agent gets only the repos you allow, an EA only the inboxes you share."
            Illustration={IntegrationsIllustration}
          />
          <Step
            reversed
            num="04"
            duration="Immediately"
            icon={Play}
            title="They start working"
            body="Every agent run is audit-logged. High-blast-radius actions wait in an approval queue. Lower-risk work ships instantly. You get a daily digest of what happened and what needs attention."
            Illustration={RunIllustration}
          />
        </div>
      </SectionContainer>

      <CtaBand />
    </>
  );
}

function Step({
  num,
  duration,
  icon: Icon,
  title,
  body,
  Illustration,
  reversed,
}: {
  num: string;
  duration: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  Illustration: React.ComponentType;
  reversed?: boolean;
}) {
  return (
    <div
      className={
        "grid lg:grid-cols-2 gap-10 lg:gap-16 items-center " +
        (reversed ? "lg:[&>div:first-child]:order-2" : "")
      }
    >
      <div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm font-bold text-brand-violet">{num}</span>
          <span className="text-xs text-brand-ink/50">—</span>
          <span className="text-xs font-medium text-brand-ink/60">{duration}</span>
        </div>
        <div className="mt-4 inline-flex rounded-lg bg-brand-violet/15 p-2.5 text-brand-violet">
          <Icon className="h-6 w-6" />
        </div>
        <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-wide text-brand-ink leading-tight">
          {title}
        </h2>
        <p className="mt-4 text-base md:text-lg text-brand-ink/70 leading-relaxed">{body}</p>
      </div>
      <div>
        <Illustration />
      </div>
    </div>
  );
}

function SubdomainIllustration() {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F17] p-6 md:p-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
        Subdomain picker
      </p>
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-brand-cyan/40 bg-white/5 px-4 py-3">
        <span className="font-mono text-lg text-brand-ink">fitfab</span>
        <span className="font-mono text-lg text-brand-ink/60">.virtualdesk.ai</span>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-brand-cyan">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden />
        Available
      </div>
      <div className="mt-6 space-y-2">
        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-brand-violet rounded-full" style={{ width: "25%" }} />
        </div>
        <p className="text-[11px] font-mono text-brand-ink/50">Step 1 of 4</p>
      </div>
    </div>
  );
}

function CatalogIllustration() {
  const items = [
    { label: "Backend Engineer", price: "$149" },
    { label: "Executive Assistant", price: "$199" },
    { label: "Ticket Responder", price: "$99" },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F17] p-6 md:p-8 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
        Hire an agent
      </p>
      {items.map((it, i) => (
        <div
          key={it.label}
          className={
            "flex items-center justify-between rounded-lg px-4 py-3 border " +
            (i === 0
              ? "border-brand-violet/40 bg-brand-violet/10"
              : "border-white/10 bg-white/[0.03]")
          }
        >
          <span className="text-sm text-brand-ink">{it.label}</span>
          <span className="font-mono text-xs text-brand-cyan">{it.price}/mo</span>
        </div>
      ))}
      <div className="mt-4 rounded-lg bg-brand-violet px-4 py-2.5 text-center text-sm font-medium text-white">
        Hire selected
      </div>
    </div>
  );
}

function IntegrationsIllustration() {
  const tools = [
    { Icon: Github, label: "GitHub", connected: true },
    { Icon: Slack, label: "Slack", connected: true },
    { Icon: Mail, label: "Gmail", connected: true },
    { Icon: Calendar, label: "Calendar", connected: false },
    { Icon: Phone, label: "Twilio", connected: false },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F17] p-6 md:p-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
        Connected tools
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {tools.map((t) => (
          <div
            key={t.label}
            className={
              "flex items-center gap-3 rounded-lg border px-3 py-2.5 " +
              (t.connected
                ? "border-brand-cyan/30 bg-brand-cyan/5"
                : "border-white/10 bg-white/[0.03]")
            }
          >
            <t.Icon className="h-5 w-5 text-brand-ink/80" />
            <span className="text-sm text-brand-ink flex-1">{t.label}</span>
            {t.connected ? (
              <span className="text-[10px] font-mono text-brand-cyan">LINKED</span>
            ) : (
              <span className="text-[10px] font-mono text-brand-ink/40">CONNECT</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RunIllustration() {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F17] p-6 md:p-8 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">
        Run log
      </p>
      <LogLine time="09:02" text="Ticket received — FITFAB-TCK-0142" color="ink" />
      <LogLine time="09:02" text="Classified: backend / auth" color="cyan" />
      <LogLine time="09:03" text="Repo cloned, branch fix/oauth-callback" color="ink" />
      <LogLine time="09:05" text="Tests added: 3, passing" color="ok" />
      <LogLine time="09:05" text="PR #1482 opened for review" color="violet" pulse />
    </div>
  );
}

function LogLine({
  time,
  text,
  color,
  pulse,
}: {
  time: string;
  text: string;
  color: "ink" | "cyan" | "ok" | "violet";
  pulse?: boolean;
}) {
  const dot =
    color === "cyan"
      ? "bg-brand-cyan"
      : color === "ok"
        ? "bg-signal-ok"
        : color === "violet"
          ? "bg-brand-violet"
          : "bg-brand-ink/30";
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
      <span className="font-mono text-[10px] text-brand-ink/50 shrink-0">{time}</span>
      <span
        className={"h-1.5 w-1.5 rounded-full shrink-0 " + dot + (pulse ? " animate-pulse" : "")}
        aria-hidden
      />
      <span className="text-xs font-mono text-brand-ink/85">{text}</span>
    </div>
  );
}
